import React from "react";
import { useInView } from "react-intersection-observer";

const CursorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M5 3l14 9-6 1.5L10 21 5 3z" fill="white" />
  </svg>
);

const MicIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.92V21h2v-3.08A7 7 0 0019 11h-2z"
      fill="white"
    />
  </svg>
);

const BoltIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="white" />
  </svg>
);

const steps = [
  {
    label: "STEP 01",
    icon: <CursorIcon />,
    title: "Click into any app",
    desc: "Open your favourite Mac app and place your cursor wherever you want to write.",
  },
  {
    label: "STEP 02",
    icon: <MicIcon />,
    title: "Speak naturally",
    desc: "Say what you want. Zero Typing listens, understands, and transcribes your words instantly.",
  },
  {
    label: "STEP 03",
    icon: <BoltIcon />,
    title: "Done. No fixing.",
    desc: "Your voice becomes clean, polished text with the right grammar, punctuation, and formatting.",
  },
];

const StepsSection = () => {
  const { ref: headRef, inView: headView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const { ref: gridRef, inView: gridView } = useInView({
    triggerOnce: true,
    threshold: 0.15,
  });
  return (
    <section className="w-full max-w-[1180px] mx-auto md:px-7 px-4 flex flex-col gap-10 md:gap-13">
      <div
        ref={headRef}
        className={`${headView ? "animate-slideUp z-[3] anim-preserve" : "opacity-0 z-0"
          } flex flex-col items-center gap-3 max-w-[720px] mx-auto`}
      >
        <h2 className="font-jakarta font-extrabold text-[#0F1B2D] text-center text-[2.625rem] md:text-[2.4rem] lg:text-[2.8rem] xl:text-[3.125rem] leading-[1.24] tracking-[-0.02em] capitalize">
          From Voice to Finished Text in{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]">
            One Breath
          </span>
        </h2>
        <p className="font-inter font-normal text-[#33425A] text-center text-[1.313rem] md:text-[1.15rem] lg:text-[1.2rem] xl:text-[1.438rem] leading-[1.5] pt-1">
          No shortcuts to memorise. No formatting to clean up. Three simple
          steps and you're done.
        </p>
      </div>

      <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-[22px]">
        {steps.map((s, i) => (
          <div
            key={i}
            style={{ animationDelay: `${i * 150}ms` }}
            className={`${gridView ? "animate-scaleIn z-[3] anim-preserve" : "opacity-0 z-0"
              } rounded-[26px] bg-[#F9F9F9] border border-[#0000001A] p-[30px] pl-7 pr-7 flex flex-col gap-2`}
          >
            <span
              className="font-jakarta font-extrabold text-[0.8125rem] tracking-[0.08em] uppercase"
              style={{ color: "#2E86E6" }}
            >
              {s.label}
            </span>
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: "linear-gradient(180deg, #3AB1F6 0%, #407BFF 100%)",
              }}
            >
              {s.icon}
            </div>
            <h3 className="font-jakarta font-bold text-[#0F1B2D] text-[1.3125rem] leading-[1.06] tracking-[-0.01em] pt-2">
              {s.title}
            </h3>
            <p className="font-inter font-normal text-[#33425A] text-[0.95rem] leading-[1.5]">
              {s.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default StepsSection;
